"use client";

import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "@/lib/utils";

export const alertVariants = cva(
  "flex items-start gap-3 rounded-[var(--r-md)] border p-3 text-sm text-[var(--foreground)]",
  {
    defaultVariants: {
      variant: "info",
    },
    variants: {
      variant: {
        error:
          "border-[var(--risk-high-line)] bg-[var(--risk-high-soft)]",
        info: "border-[var(--line)] bg-[var(--brand-soft)]",
        success: "border-[var(--risk-low-line)] bg-[var(--risk-low-soft)]",
        warning: "border-[var(--risk-med-line)] bg-[var(--risk-med-soft)]",
      },
    },
  },
);

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {}

export function Alert({
  className,
  variant = "info",
  children,
  ...props
}: AlertProps): React.ReactElement {
  return (
    <div
      role={variant === "error" ? "alert" : "status"}
      className={cn(alertVariants({ variant }), className)}
      data-slot="alert"
      {...props}
    >
      {children}
    </div>
  );
}

export function AlertTitle({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
  return (
    <div
      className={cn("text-sm font-semibold leading-tight", className)}
      data-slot="alert-title"
      {...props}
    />
  );
}

export function AlertDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
  return (
    <div
      className={cn("text-sm", className)}
      data-slot="alert-description"
      {...props}
    />
  );
}

export function AlertAction({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
  return (
    <div
      className={cn("ml-auto flex shrink-0 items-center", className)}
      data-slot="alert-action"
      {...props}
    />
  );
}
